import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tenantApi, Integration, WordPressProduct, WordPressPost, ImportResult } from '../lib/tenant-api';
import { toast } from 'react-toastify';
import Modal from './Modal';
import { PageLoader } from './common';
import { Check, ExternalLink, Package, FileText, ChevronDown, ChevronRight } from 'lucide-react';
import { useEventNotifier } from '../contexts/EventNotifierContext';

interface WordPressImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  integration: Integration;
  contentType: 'products' | 'services' | 'documents';
}

const WordPressImportModal: React.FC<WordPressImportModalProps> = ({
  isOpen,
  onClose,
  integration,
  contentType,
}) => {
  const queryClient = useQueryClient();
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [expandedEndpoints, setExpandedEndpoints] = useState<Set<string>>(new Set());
  const [endpointSelections, setEndpointSelections] = useState<Record<string, string[]>>({});
  const [generateEmbeddings, setGenerateEmbeddings] = useState(true);
  const [items, setItems] = useState<any[]>([]);
  const { emit } = useEventNotifier();
  // Fetch WordPress content based on content type
  const { data: wordPressData, isLoading, error } = useQuery({
    queryKey: ['wordpress-content', integration.id, contentType],
    queryFn: async () => {
      if (contentType === 'products') {
        return await tenantApi.getWordPressProducts(integration.id);
      } else if (contentType === 'services') {
        return await tenantApi.getWordPressServices(integration.id);
      } else if (contentType === 'documents') {
        return await tenantApi.getWordPressPosts(integration.id);
      }
      return { data: [], success: false, message: 'Invalid content type' };
    },
    enabled: isOpen,
  });

  const importMutation = useMutation({
    mutationFn: tenantApi.importWordPressContent,
    onSuccess: (result) => {
      setImportResult(result);
      // Invalidate relevant queries to refresh the data
      if (contentType === 'products') {
        queryClient.invalidateQueries({ queryKey: ['products'] });
      } else if (contentType === 'services') {
        queryClient.invalidateQueries({ queryKey: ['services'] });
      } else if (contentType === 'documents') {
        queryClient.invalidateQueries({ queryKey: ['documents'] });
      }

      if (result.successful_imports > 0) {
        toast.success(`Successfully imported ${result.successful_imports} items!`);
      }
      if (result.failed_imports > 0) {
        toast.warning(`${result.failed_imports} items failed to import.`);
      }

      emit('refresh', { message: contentType });
    },
    onError: (error: any) => {
      toast.error(`Import failed: ${error.message}`);
    },
  });

  useEffect(() => {
    if (!isOpen) {
      setSelectedItems([]);
      setImportResult(null);
      setExpandedEndpoints(new Set());
      setEndpointSelections({});
    }
  }, [isOpen]);

  useEffect(() => {
    if (wordPressData) {
      if (wordPressData.success === false) {
        toast.error(wordPressData.message || 'Failed to fetch WordPress content.');
      }

      const items = contentType === 'documents'
        ? (wordPressData.data as WordPressPost[]) || []
        : contentType === 'services'
          ? (wordPressData.data as WordPressProduct[]) || []
          : (wordPressData.data as WordPressProduct[]) || [];

      setItems(items);
    }
  }, [wordPressData]);


  // Group items by source endpoint
  const itemsByEndpoint = items.reduce((acc, item) => {
    const endpoint = (item as any).source_endpoint || 'default';
    if (!acc[endpoint]) {
      acc[endpoint] = [];
    }
    acc[endpoint].push(item);
    return acc;
  }, {} as Record<string, (WordPressProduct | WordPressPost)[]>);

  const endpoints = Object.keys(itemsByEndpoint);

  // Initialize expanded state for all endpoints when data loads
  useEffect(() => {
    if (items.length > 0 && expandedEndpoints.size === 0) {
      setExpandedEndpoints(new Set(endpoints));
    }
  }, [items.length, endpoints, expandedEndpoints.size]);

  useEffect(() => {
    if (items.length === 0) {
      return;
    }
    setSelectedItems(items.map(item => item.id));
  }, [items])

  // Initialize endpoint selections when data loads
  useEffect(() => {
    if (items.length > 0 && Object.keys(endpointSelections).length === 0) {
      const initialSelections: Record<string, string[]> = {};
      Object.keys(itemsByEndpoint).forEach(endpoint => {
        initialSelections[endpoint] = [];
      });
      setEndpointSelections(initialSelections);
    }
  }, [items.length, itemsByEndpoint, endpointSelections]);

  const handleSelectAll = () => {
    if (selectedItems.length === items.length) {
      // Deselect all
      setSelectedItems([]);
      const newEndpointSelections: Record<string, string[]> = {};
      Object.keys(itemsByEndpoint).forEach(endpoint => {
        newEndpointSelections[endpoint] = [];
      });
      setEndpointSelections(newEndpointSelections);
    } else {
      // Select all
      setSelectedItems(items.map(item => item.id));
      const newEndpointSelections: Record<string, string[]> = {};
      Object.keys(itemsByEndpoint).forEach(endpoint => {
        newEndpointSelections[endpoint] = itemsByEndpoint[endpoint].map((item: { id: any; }) => item.id);
      });
      setEndpointSelections(newEndpointSelections);
    }
  };

  const handleSelectAllForEndpoint = (endpoint: string) => {
    const endpointItems = itemsByEndpoint[endpoint] || [];
    const currentlySelected = endpointSelections[endpoint] || [];

    if (currentlySelected.length === endpointItems.length) {
      // Deselect all for this endpoint
      const newEndpointSelections = {
        ...endpointSelections,
        [endpoint]: []
      };
      setEndpointSelections(newEndpointSelections);

      // Update global selection
      const newSelectedItems = selectedItems.filter(id =>
        !endpointItems.some((item: { id: string; }) => item.id === id)
      );
      setSelectedItems(newSelectedItems);
    } else {
      // Select all for this endpoint
      const endpointItemIds = endpointItems.map((item: { id: any; }) => item.id);
      const newEndpointSelections = {
        ...endpointSelections,
        [endpoint]: endpointItemIds
      };
      setEndpointSelections(newEndpointSelections);

      // Update global selection
      const newSelectedItems = [...new Set([...selectedItems, ...endpointItemIds])];
      setSelectedItems(newSelectedItems);
    }
  };

  const toggleEndpointExpansion = (endpoint: string) => {
    const newExpanded = new Set(expandedEndpoints);
    if (newExpanded.has(endpoint)) {
      newExpanded.delete(endpoint);
    } else {
      newExpanded.add(endpoint);
    }
    setExpandedEndpoints(newExpanded);
  };

  const handleSelectItem = (itemId: string, endpoint: string) => {
    const currentEndpointSelections = endpointSelections[endpoint] || [];
    const isCurrentlySelected = currentEndpointSelections.includes(itemId);

    if (isCurrentlySelected) {
      // Remove from endpoint and global selection
      const newEndpointSelections = {
        ...endpointSelections,
        [endpoint]: currentEndpointSelections.filter(id => id !== itemId)
      };
      setEndpointSelections(newEndpointSelections);
      setSelectedItems(prev => prev.filter(id => id !== itemId));
    } else {
      // Add to endpoint and global selection
      const newEndpointSelections = {
        ...endpointSelections,
        [endpoint]: [...currentEndpointSelections, itemId]
      };
      setEndpointSelections(newEndpointSelections);
      setSelectedItems(prev => [...prev, itemId]);
    }
  };

  const handleImport = () => {
    if (selectedItems.length === 0) {
      toast.warning('Please select at least one item to import.');
      return;
    }

    // const importContentType = contentType === 'documents' ? 'posts' : contentType;
    const importContentType = contentType;

    importMutation.mutate({
      integration_id: integration.id,
      content_type: importContentType,
      selected_items: selectedItems,
      generate_embeddings: generateEmbeddings,
    });
  };

  const getItemTitle = (item: WordPressProduct | WordPressPost) => {
    return 'name' in item ? item.name : item.title;
  };

  const getItemDescription = (item: WordPressProduct | WordPressPost) => {
    if ('description' in item) {
      return item.description || item.short_description || '';
    }
    return item.excerpt || '';
  };

  const getItemUrl = (item: WordPressProduct | WordPressPost) => {
    return 'permalink' in item ? item.permalink : item.link;
  };

  const renderItem = (item: WordPressProduct | WordPressPost, endpoint: string) => {
    const title = getItemTitle(item);
    const description = getItemDescription(item);
    const url = getItemUrl(item);
    const isSelected = selectedItems.includes(item.id);

    return (
      <div
        key={item.id}
        className={`flex items-start p-3 border  cursor-pointer transition-colors ${isSelected
          ? 'border-indigo-300 bg-indigo-50'
          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
          }`}
        onClick={() => handleSelectItem(item.id, endpoint)}
      >
        <div className="flex-shrink-0 mt-0.5">
          <div className={`w-4 h-4 border-2  flex items-center justify-center ${isSelected
            ? 'border-indigo-500 bg-indigo-500'
            : 'border-gray-300'
            }`}>
            {isSelected && <Check className="h-3 w-3 text-white" />}
          </div>
        </div>

        <div className="ml-3 flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-gray-900 truncate">
              {title}
            </h4>
            <div className="flex items-center space-x-2">
              {url && (
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
              )}
            </div>
          </div>

          {description && (
            <p className="mt-1 text-sm text-gray-500 line-clamp-2">
              {description.substring(0, 150)}
              {description.length > 150 ? '...' : ''}
            </p>
          )}

          {contentType === 'products' && 'price' in item && item.price && item.price !== '0' && (
            <p className="mt-1 text-sm font-medium text-green-600">
              ${parseFloat(item.price).toFixed(2)}
            </p>
          )}

          {contentType === 'documents' && 'date' in item && (
            <p className="mt-1 text-xs text-gray-400">
              Published: {new Date(item.date).toLocaleDateString()}
            </p>
          )}
        </div>
      </div>
    );
  };

  const getContentIcon = () => {
    switch (contentType) {
      case 'products':
      case 'services':
        return <Package className="h-6 w-6 text-indigo-600" />;
      case 'documents':
        return <FileText className="h-6 w-6 text-indigo-600" />;
      default:
        return <Package className="h-6 w-6 text-indigo-600" />;
    }
  };

  const getContentTypeLabel = () => {
    switch (contentType) {
      case 'products':
        return 'Products';
      case 'services':
        return 'Services';
      case 'documents':
        return 'Documents';
      default:
        return 'Content';
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Import ${getContentTypeLabel()} from ${integration.name}`}
      size="xl"
    >
      <div className="space-y-6">
        {!importResult ? (
          <>
            {/* Header */}
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-indigo-100  flex items-center justify-center">
                {getContentIcon()}
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  {integration.integration_type === 'google_drive' ? 'Google Drive Files' : `Available ${getContentTypeLabel()}`}
                </h3>
                <p className="text-sm text-gray-500">
                  Select the {integration.integration_type === 'google_drive' ? 'files' : contentType} you want to import from {integration.name}.
                </p>
              </div>
            </div>

            {isLoading && (
              <PageLoader fullScreen={false} />
            )}

            {/* Error State */}
            {error && (
              <div className="bg-red-50 border border-red-200  p-4">
                <p className="text-sm text-red-800">
                  Failed to fetch {contentType} from {integration.name}. Please check your integration settings.
                </p>
              </div>
            )}

            {/* Content List */}
            {!isLoading && !error && items.length > 0 && (
              <>
                {/* Select All */}
                <div className="flex items-center justify-between border-b border-gray-200 pb-4">
                  <button
                    onClick={handleSelectAll}
                    className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
                  >
                    {selectedItems.length === items.length ? 'Deselect All' : 'Select All'}
                  </button>
                  <span className="text-sm text-gray-500">
                    {selectedItems.length} of {items.length} selected
                  </span>
                </div>

                {/* Items by Endpoint */}
                <div className="max-h-96 overflow-y-auto space-y-4">
                  {endpoints.map(endpoint => {
                    const endpointItems = itemsByEndpoint[endpoint];
                    const isExpanded = expandedEndpoints.has(endpoint);
                    const selectedInEndpoint = endpointSelections[endpoint] || [];
                    const endpointName = endpoint.split('/').pop() || endpoint;

                    return (
                      <div key={endpoint} className="border border-gray-200 ">
                        {/* Endpoint Header */}
                        <div
                          className="flex items-center justify-between p-4 bg-gray-50 cursor-pointer"
                          onClick={() => toggleEndpointExpansion(endpoint)}
                        >
                          <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                              {isExpanded ? (
                                <ChevronDown className="h-4 w-4 text-gray-500" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-gray-500" />
                              )}
                            </div>
                            <div>
                              <h4 className="text-sm font-medium text-gray-900">
                                {endpointName}
                              </h4>
                              <p className="text-xs text-gray-500">
                                {endpoint}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            <span className="text-xs text-gray-500">
                              {selectedInEndpoint.length} of {endpointItems.length} selected
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSelectAllForEndpoint(endpoint);
                              }}
                              className="text-xs font-medium text-indigo-600 hover:text-indigo-500"
                            >
                              {selectedInEndpoint.length === endpointItems.length ? 'Deselect All' : 'Select All'}
                            </button>
                          </div>
                        </div>

                        {/* Endpoint Items */}
                        {isExpanded && (
                          <div className="p-3 space-y-2 border-t border-gray-100">
                            {endpointItems.map((item: WordPressProduct | WordPressPost) => renderItem(item, endpoint))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* Empty State */}
            {!isLoading && !error && items.length === 0 && (
              <div className="text-center py-12">
                {getContentIcon()}
                <h3 className="mt-4 text-lg font-medium text-gray-900">
                  No {contentType} found
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  No {contentType} were found in your {integration.integration_type} site.
                </p>
              </div>
            )}

            {/* Embedding Options */}
            <div className="pt-6 border-t border-gray-200">
              <div className="flex items-center">
                <input
                  id="generate-embeddings"
                  name="generate-embeddings"
                  type="checkbox"
                  className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 "
                  checked={generateEmbeddings}
                  onChange={(e) => setGenerateEmbeddings(e.target.checked)}
                />
                <label htmlFor="generate-embeddings" className="ml-3 text-sm font-medium text-gray-700">
                  Generate AI embeddings for imported content
                </label>
              </div>
              <p className="mt-1 text-xs text-gray-500 ml-7">
                This enables AI-powered search and recommendations for the imported content.
              </p>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300  hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleImport}
                disabled={selectedItems.length === 0 || importMutation.isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent  hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {importMutation.isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white inline" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {generateEmbeddings ? 'Importing & Generating Embeddings...' : 'Importing...'}
                  </>
                ) : (
                  `Import ${selectedItems.length} ${contentType}${generateEmbeddings ? ' + Embeddings' : ''}`
                )}
              </button>
            </div>
          </>
        ) : (
          /* Import Results */
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <Check className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">Import Complete</h3>
              <p className="mt-2 text-sm text-gray-500">
                Import process finished with {importResult.success_rate.toFixed(1)}% success rate
              </p>
            </div>

            {/* Results Summary */}
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-green-50 ">
                <div className="text-2xl font-bold text-green-600">{importResult.successful_imports}</div>
                <div className="text-sm text-green-800">Successful Imports</div>
              </div>
              <div className="text-center p-4 bg-red-50 ">
                <div className="text-2xl font-bold text-red-600">{importResult.failed_imports}</div>
                <div className="text-sm text-red-800">Failed Imports</div>
              </div>
            </div>

            {/* Embedding Results */}
            {importResult.embedding_generation && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-800 mb-3">Embedding Generation Results</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-indigo-50 ">
                    <div className="text-xl font-bold text-indigo-600">{importResult.embedding_generation.successful}</div>
                    <div className="text-xs text-indigo-800">Embeddings Created</div>
                  </div>
                  <div className="text-center p-3 bg-orange-50 ">
                    <div className="text-xl font-bold text-orange-600">{importResult.embedding_generation.failed}</div>
                    <div className="text-xs text-orange-800">Embedding Failures</div>
                  </div>
                </div>
                {importResult.embedding_generation.error && (
                  <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200  text-xs text-yellow-800">
                    <strong>Embedding Error:</strong> {importResult.embedding_generation.error}
                  </div>
                )}
              </div>
            )}

            {/* Error Details */}
            {importResult.errors.length > 0 && (
              <div className="bg-red-50 border border-red-200  p-4">
                <h4 className="text-sm font-medium text-red-800 mb-2">
                  Import Errors ({importResult.errors.length})
                </h4>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {importResult.errors.slice(0, 5).map((error, index) => (
                    <div key={index} className="text-sm text-red-700">
                      {error.message}
                    </div>
                  ))}
                  {importResult.errors.length > 5 && (
                    <div className="text-sm text-red-600 italic">
                      ...and {importResult.errors.length - 5} more errors
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="flex justify-end pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent  hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default WordPressImportModal;
